import React, { useState } from "react";
import { Link } from "react-router-dom";
import PageTopSlider from "../components/PageTopSlider";
import ReviewsSlider from "../components/ReviewsSlider";
import ContactForm from "../components/ContactForm";
import SEO from "../components/SEO";

export default function Prices() {
  const [activeCategory, setActiveCategory] = useState("enfamilie");

  const priceItems = [
    {
      service: "Energimærkning",
      price: "Fra 4.188 kr. inkl. moms",
      desc: "Gennemgang af ejendommen, beregninger samt udarbejdelse af officiel rapport med besparelsesforslag. Gyldig i 10 år.",
      path: "/energimaerkning",
      icon: "fa-certificate",
    },
    {
      service: "Trykprøvning / blowerdoor",
      price: "Fra 3.300 kr. inkl. moms",
      desc: "Måling af bygningens lufttæthed med professionelt kalibreret BlowerDoor udstyr. Komplet dokumentation til bygningsreglement.",
      path: "/trykproevning",
      icon: "fa-wind",
    },
    {
      service: "Termografi",
      price: "Fra 2.500 kr. inkl. moms",
      desc: "Infrarød termografering med varmekamera. Afslører kuldebroer, utætheder og isoleringsmangler i bygningen.",
      path: "/termografi",
      icon: "fa-temperature-arrow-down",
    },
    {
      service: "Energiramme",
      price: "Få et gratis tilbud",
      desc: "Beregning af maksimalt energiforbrug pr. m² pr. år til byggeansøgning og overholdelse af bygningsreglementet.",
      path: "/energiramme",
      icon: "fa-compass-drafting",
    },
  ];

  const kombiTiers = [
    {
      size: "0–99 m²",
      normalPrice: "7.488 kr.",
      kombiPrice: "6.888 kr. inkl. moms",
      savings: "600 kr.",
    },
    {
      size: "100–199 m²",
      normalPrice: "7.788 kr.",
      kombiPrice: "7.188 kr. inkl. moms",
      savings: "600 kr.",
    },
    {
      size: "200–299 m²",
      normalPrice: "8.188 kr.",
      kombiPrice: "7.588 kr. inkl. moms",
      savings: "600 kr.",
    },
    {
      size: "300 m² og derover",
      normalPrice: "Fra 8.800 kr.",
      kombiPrice: "Fra 8.200 kr. inkl. moms",
      savings: "600 kr.",
    },
  ];

  return (
    <div className="bg-[#FAF9F6] text-stone-800">
      <SEO
        title="Priser | ET Danmark ApS"
        description="Vejledende priser: Energimærkning fra 4.188 kr. inkl. moms, trykprøvning fra 3.300 kr. inkl. moms, termografi fra 2.500 kr. inkl. moms. Kombipakke fra 6.888 kr. inkl. moms (spar 600 kr.). Få et tilbud hos ET Danmark."
        canonical="https://etdanmark.com/priser"
        ogImage="https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07613.jpg"
      />
      {/* Header */}
      <PageTopSlider
        title="Priser"
        subtitle="Vi tror på gennemsigtige og konkurrencedygtige priser. Herunder finder du vores vejledende fra-priser for energirådgivning, målinger og rapporter."
        badge="Gennemskuelige priser inkl. moms"
        breadcrumbs={[
          { label: "Forside", to: "/" },
          { label: "Priser" },
        ]}
        image={{
          url: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07445.jpg",
          alt: "Gennemskuelige priser på energirådgivning",
        }}
        actions={
          <>
            <Link
              to="/prisberegner"
              className="px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-calculator text-xs" aria-hidden="true"></i>
              <span>Beregn din pris online</span>
            </Link>
            <a
              href="#kontakt"
              className="px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-bold text-sm backdrop-blur-xs transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-envelope text-emerald-300 text-xs" aria-hidden="true"></i>
              <span>Få et skræddersyet tilbud</span>
            </a>
          </>
        }
      />

      {/* Prisoversigt Tabel og Cards */}
      <section className="py-16 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-stone-900">Service & Vejledende fra-priser</h2>
            <p className="text-stone-600 text-sm mt-1">
              Alle anførte priser er inklusive moms, kørsel i Jylland & Fyn samt komplet officiel rapport.
            </p>
          </div>

          {/* Grid Layout of Pricing */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {priceItems.map((item, idx) => (
              <div
                key={idx}
                className="p-6 sm:p-8 rounded-2xl bg-[#FAF9F6] border border-stone-200 hover:border-emerald-300 transition-colors flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#165B4C]/10 text-[#165B4C] flex items-center justify-center text-lg shrink-0">
                        <i className={`fa-solid ${item.icon}`} aria-hidden="true"></i>
                      </div>
                      <h3 className="text-xl font-bold text-stone-900">{item.service}</h3>
                    </div>
                  </div>

                  <p className="text-stone-600 text-sm leading-relaxed mb-6">
                    {item.desc}
                  </p>
                </div>

                <div className="pt-4 border-t border-stone-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <span className="text-xs text-stone-500 block">Vejledende pris</span>
                    <span className="text-2xl font-extrabold text-[#165B4C]">
                      {item.price}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to={item.path}
                      className="px-4 py-2 rounded-full bg-white border border-stone-300 hover:bg-stone-50 text-xs font-semibold text-stone-800 transition-colors"
                    >
                      Læs mere
                    </Link>
                    <Link
                      to="/prisberegner"
                      className="px-4 py-2 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-xs font-semibold text-white transition-colors shadow-2xs"
                    >
                      Beregn
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* DETALJERET PRISOVERSIGT FOR ENERGIMÆRKER */}
          <div className="mt-16 pt-12 border-t border-stone-200">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-[#165B4C] text-xs font-bold uppercase tracking-wider mb-2">
                  <i className="fa-solid fa-list-check" aria-hidden="true"></i>
                  <span>Komplet prisliste</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
                  Priser på Energimærker
                </h3>
                <p className="text-stone-600 text-sm mt-1 max-w-2xl">
                  Se oversigten over priser for energimærkning inddelt efter bygningstype og etageareal.
                </p>
              </div>

              {/* Kategori knapper */}
              <div className="flex flex-wrap gap-2 p-1 bg-stone-100 rounded-xl border border-stone-200">
                {[
                  { id: "enfamilie", label: "Enfamiliehuse" },
                  { id: "raekkehus", label: "Rækkehuse" },
                  { id: "flerfamilie", label: "Flerfamilieejendomme" },
                  { id: "erhverv", label: "Erhvervsejendomme" },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveCategory(tab.id)}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                      activeCategory === tab.id
                        ? "bg-white text-[#165B4C] shadow-xs"
                        : "text-stone-600 hover:text-stone-900"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* TAB 1: Enfamiliehuse */}
            {activeCategory === "enfamilie" && (
              <div className="bg-[#FAF9F6] border border-stone-200 rounded-2xl overflow-hidden shadow-xs">
                <div className="p-5 bg-stone-100/70 border-b border-stone-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#165B4C] text-white flex items-center justify-center text-sm">
                      <i className="fa-solid fa-house" aria-hidden="true"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-stone-900 text-base">Enfamiliehuse</h4>
                      <p className="text-xs text-stone-500">Villaer, parcelhuse og enfamiliehuse opgjort efter boligareal.</p>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-emerald-800 bg-emerald-100 px-2.5 py-1 rounded-full">
                    Alle priser inkl. moms
                  </span>
                </div>
                <div className="divide-y divide-stone-200">
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors">
                    <div>
                      <span className="font-bold text-stone-900 text-sm sm:text-base">0–99 m²</span>
                      <p className="text-xs text-stone-500">Mindre boliger, sommerhuse og rækkehuse</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">4.188 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms</span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors">
                    <div>
                      <span className="font-bold text-stone-900 text-sm sm:text-base">100–199 m²</span>
                      <p className="text-xs text-stone-500">Typisk parcelhusstørrelse</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">4.488 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms</span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors">
                    <div>
                      <span className="font-bold text-stone-900 text-sm sm:text-base">200–299 m²</span>
                      <p className="text-xs text-stone-500">Større villaer og enfamiliehuse</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">4.888 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms</span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors bg-emerald-50/30">
                    <div>
                      <span className="font-bold text-stone-900 text-sm sm:text-base">300 m² og derover</span>
                      <p className="text-xs text-stone-500">Meget store villaer eller landejendomme</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">Fra 5.500 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2: Rækkehuse */}
            {activeCategory === "raekkehus" && (
              <div className="bg-[#FAF9F6] border border-stone-200 rounded-2xl overflow-hidden shadow-xs">
                <div className="p-5 bg-stone-100/70 border-b border-stone-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#165B4C] text-white flex items-center justify-center text-sm">
                      <i className="fa-solid fa-houses" aria-hidden="true"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-stone-900 text-base">Rækkehuse</h4>
                      <p className="text-xs text-stone-500">Storkunderabat ved samlet energimærkning af ensartede rækkehuse.</p>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-emerald-800 bg-emerald-100 px-2.5 py-1 rounded-full">
                    Pris pr. stk. inkl. moms
                  </span>
                </div>
                <div className="divide-y divide-stone-200">
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors">
                    <div>
                      <span className="font-bold text-stone-900 text-sm sm:text-base">3–5 ensartede rækkehuse</span>
                      <p className="text-xs text-stone-500">Pris pr. stk. ved samlet gennemgang</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">2.488 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms pr. stk.</span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors">
                    <div>
                      <span className="font-bold text-stone-900 text-sm sm:text-base">6–9 ensartede rækkehuse</span>
                      <p className="text-xs text-stone-500">Pris pr. stk. ved samlet gennemgang</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">1.888 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms pr. stk.</span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors bg-emerald-50/30">
                    <div>
                      <span className="font-bold text-stone-900 text-sm sm:text-base">10 eller flere ensartede rækkehuse</span>
                      <p className="text-xs text-stone-500">Pris pr. stk. ved større rækkehusbebyggelser</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">Fra 1.588 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms pr. stk.</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 3: Flerfamilieejendomme */}
            {activeCategory === "flerfamilie" && (
              <div className="bg-[#FAF9F6] border border-stone-200 rounded-2xl overflow-hidden shadow-xs">
                <div className="p-5 bg-stone-100/70 border-b border-stone-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#165B4C] text-white flex items-center justify-center text-sm">
                      <i className="fa-solid fa-city" aria-hidden="true"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-stone-900 text-base">Flerfamilieejendomme</h4>
                      <p className="text-xs text-stone-500">Etageejendomme, andels- og ejerforeninger samt udlejningsejendomme.</p>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-emerald-800 bg-emerald-100 px-2.5 py-1 rounded-full">
                    Vejledende fra-priser inkl. moms
                  </span>
                </div>
                <div className="divide-y divide-stone-200">
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors">
                    <span className="font-bold text-stone-900 text-sm sm:text-base">0–499 m²</span>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">Fra 6.688 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms</span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors">
                    <span className="font-bold text-stone-900 text-sm sm:text-base">500–999 m²</span>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">Fra 8.088 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms</span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors">
                    <span className="font-bold text-stone-900 text-sm sm:text-base">1.000–1.999 m²</span>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">Fra 9.288 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms</span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors">
                    <span className="font-bold text-stone-900 text-sm sm:text-base">2.000–2.999 m²</span>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">Fra 10.888 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms</span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors">
                    <span className="font-bold text-stone-900 text-sm sm:text-base">3.000–4.999 m²</span>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">Fra 14.888 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms</span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors bg-emerald-50/40">
                    <span className="font-bold text-stone-900 text-sm sm:text-base">5.000 m² og derover</span>
                    <div className="text-right">
                      <a
                        href="#kontakt"
                        className="px-4 py-2 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white font-bold text-xs inline-flex items-center gap-1.5 transition-colors"
                      >
                        <span>Kontakt os for et tilbud</span>
                        <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 4: Erhvervsejendomme */}
            {activeCategory === "erhverv" && (
              <div className="bg-[#FAF9F6] border border-stone-200 rounded-2xl overflow-hidden shadow-xs">
                <div className="p-5 bg-stone-100/70 border-b border-stone-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#165B4C] text-white flex items-center justify-center text-sm">
                      <i className="fa-solid fa-building" aria-hidden="true"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-stone-900 text-base">Erhvervsejendomme</h4>
                      <p className="text-xs text-stone-500">Kontor, butik, lager og blandet erhverv.</p>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-emerald-800 bg-emerald-100 px-2.5 py-1 rounded-full">
                    Vejledende fra-priser inkl. moms
                  </span>
                </div>
                <div className="divide-y divide-stone-200">
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors">
                    <span className="font-bold text-stone-900 text-sm sm:text-base">0–499 m²</span>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">Fra 7.488 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms</span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors">
                    <span className="font-bold text-stone-900 text-sm sm:text-base">500–999 m²</span>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">Fra 8.688 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms</span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors">
                    <span className="font-bold text-stone-900 text-sm sm:text-base">1.000–1.999 m²</span>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">Fra 9.888 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms</span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors">
                    <span className="font-bold text-stone-900 text-sm sm:text-base">2.000–2.999 m²</span>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">Fra 12.388 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms</span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors">
                    <span className="font-bold text-stone-900 text-sm sm:text-base">3.000–4.999 m²</span>
                    <div className="text-right">
                      <span className="text-xl sm:text-2xl font-extrabold text-[#165B4C]">Fra 14.888 kr.</span>
                      <span className="text-xs text-stone-500 block">inkl. moms</span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 flex items-center justify-between hover:bg-white transition-colors bg-emerald-50/40">
                    <span className="font-bold text-stone-900 text-sm sm:text-base">5.000 m² og derover</span>
                    <div className="text-right">
                      <a
                        href="#kontakt"
                        className="px-4 py-2 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white font-bold text-xs inline-flex items-center gap-1.5 transition-colors"
                      >
                        <span>Kontakt os for et tilbud</span>
                        <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Betingelsesnote */}
            <p className="mt-4 text-xs text-stone-500 italic leading-relaxed">
              Alle priser er inkl. moms. Priserne for flerfamilie- og erhvervsejendomme er vejledende fra-priser og gælder ejendomme med almindelige bygnings- og installationsforhold. Ved særligt komplekse ejendomme udarbejdes et individuelt tilbud.
            </p>
          </div>

          {/* Kombi-pakke highlight */}
          <div className="mt-12 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-[#11463A] to-[#165B4C] text-white shadow-xl relative overflow-hidden">
            <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-400/20 text-emerald-200 border border-emerald-400/30 text-xs font-semibold uppercase tracking-wider">
                  <i className="fa-solid fa-sparkles text-[11px]" aria-hidden="true"></i>
                  <span>Populært valg – Spar 600 kr.</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                  Kombipakke: Energimærkning + Trykprøvning
                </h3>
                <p className="text-stone-200 text-sm max-w-2xl leading-relaxed">
                  Få udført energimærkning og trykprøvning samlet ved samme besøg og spar 600 kr. Det sparer kørsel og administration – og giver dig en samlet fordelagtig pris.
                </p>
                <div className="flex flex-wrap items-center gap-4 pt-1 text-xs text-stone-200">
                  <span className="flex items-center gap-1.5">
                    <i className="fa-solid fa-circle-check text-emerald-300" aria-hidden="true"></i>
                    Lovpligtig 10-års energimærkning
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="fa-solid fa-circle-check text-emerald-300" aria-hidden="true"></i>
                    Kalibreret BlowerDoor tæthedsrapport
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="fa-solid fa-circle-check text-emerald-300" aria-hidden="true"></i>
                    Prisen afhænger af boligens størrelse
                  </span>
                </div>
              </div>

              <div className="shrink-0 bg-white/10 backdrop-blur-xs border border-white/20 p-6 rounded-2xl text-center flex flex-col justify-center min-w-[260px]">
                <div className="text-xs text-stone-300 line-through">
                  Normalpris fra 7.488 kr.
                </div>
                <div className="text-3xl sm:text-4xl font-extrabold text-white mt-0.5">
                  Fra 6.888 kr.
                </div>
                <div className="text-xs text-emerald-300 font-semibold mt-1">
                  inkl. moms • Spar 600 kr.
                </div>
                <Link
                  to="/prisberegner"
                  className="mt-4 px-6 py-2.5 rounded-full bg-white hover:bg-stone-100 text-[#11463A] font-bold text-xs inline-flex items-center justify-center gap-2 transition-all shadow-md"
                >
                  <i className="fa-solid fa-calculator text-xs" aria-hidden="true"></i>
                  <span>Beregn kombipakke</span>
                </Link>
              </div>
            </div>

            {/* Tabel over kombipriser pr. størrelse */}
            <div className="mt-8 pt-6 border-t border-white/20">
              <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-300 mb-3">
                Kombipriser efter boligens areal (Enfamiliehuse):
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {kombiTiers.map((tier, i) => (
                  <div
                    key={i}
                    className="p-4 rounded-xl bg-white/10 border border-white/15 backdrop-blur-xs flex flex-col justify-between"
                  >
                    <div>
                      <span className="text-xs font-semibold text-emerald-200 block">{tier.size}</span>
                      <span className="text-xs text-stone-300 line-through block mt-1">
                        Normalpris: {tier.normalPrice}
                      </span>
                      <span className="text-lg font-bold text-white block mt-0.5">
                        {tier.kombiPrice}
                      </span>
                    </div>
                    <div className="mt-2 pt-2 border-t border-white/10 flex items-center justify-between text-xs">
                      <span className="text-stone-300">Besparelse:</span>
                      <span className="text-emerald-300 font-bold">{tier.savings}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Vigtige prisbetingelser & Trust */}
          <div className="mt-12 p-6 rounded-2xl bg-stone-50 border border-stone-200 grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs text-stone-700">
            <div className="flex items-center gap-2.5">
              <i className="fa-solid fa-circle-check text-emerald-600 text-sm shrink-0" aria-hidden="true"></i>
              <span>Alle priser er inkl. moms</span>
            </div>
            <div className="flex items-center gap-2.5">
              <i className="fa-solid fa-circle-check text-emerald-600 text-sm shrink-0" aria-hidden="true"></i>
              <span>Inkl. lovpligtig rapport og dokumentation</span>
            </div>
            <div className="flex items-center gap-2.5">
              <i className="fa-solid fa-circle-check text-emerald-600 text-sm shrink-0" aria-hidden="true"></i>
              <span>Ingen skjulte gebyrer eller uforudsete tillæg</span>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Slider */}
      <ReviewsSlider />

      {/* Kontakt Sektion */}
      <section id="kontakt" className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mx-auto mb-8 text-center">
            <h2 className="text-3xl font-bold text-stone-900">Kontakt os</h2>
            <p className="text-stone-600 text-sm mt-1">
              Du kan let og hurtigt sende os en besked i nedenstående formular.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <ContactForm
              title="Få et uforpligtende tilbud på din opgave"
              subtitle="Skriv til os herunder, så vender vi hurtigt tilbage."
            />
          </div>
        </div>
      </section>
    </div>
  );
}
